// frontend/src/stores/geofenceStore.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import { useAuthStore } from './auth'

export const useGeofenceStore = defineStore('geofence', () => {
  const authStore = useAuthStore()
  
  // State
  const geofences = ref([])
  const drawings = ref([])
  const history = ref([])
  const alerts = ref([])
  const activeShips = ref([])
  const loading = ref(false)
  const error = ref(null)

  // API URL
  const API_URL = import.meta.env.VITE_API_URL || 'http://31.97.58.184:4002'

  // Helper untuk mendapatkan headers dengan token
  const getAuthHeaders = () => {
    const token = authStore.token || localStorage.getItem('token')
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  }

  // Fetch semua geofences dari backend
  async function fetchGeofences() {
    loading.value = true
    error.value = null
    try {
      const response = await axios.get(`${API_URL}/api/geofences`, getAuthHeaders())
      if (response.data.success) {
        geofences.value = response.data.geofences || []
        return geofences.value
      }
    } catch (err) {
      console.error('Failed to fetch geofences:', err)
      error.value = err.response?.data?.message || err.message
    } finally {
      loading.value = false
    }
    return []
  }

  // Fetch drawings
  async function fetchDrawings() {
    try {
      const response = await axios.get(`${API_URL}/api/drawings`, getAuthHeaders())
      if (response.data.success) {
        drawings.value = response.data.drawings || []
      }
    } catch (err) {
      console.error('Failed to fetch drawings:', err)
    }
  }

  // Save geofence ke backend
  async function saveGeofence(name, geojson) {
    try {
      const response = await axios.post(`${API_URL}/api/geofences`, 
        { name, geojson }, 
        getAuthHeaders()
      )
      if (response.data.success) {
        await fetchGeofences()
        return { success: true, id: response.data.id }
      }
      return { success: false, error: response.data.error }
    } catch (err) {
      console.error('Failed to save geofence:', err)
      return { success: false, error: err.response?.data?.message || err.message }
    }
  }

  // Delete geofence
  async function deleteGeofence(id) {
    try {
      const response = await axios.delete(`${API_URL}/api/geofences/${id}`, getAuthHeaders())
      if (response.data.success) {
        await fetchGeofences()
        return { success: true }
      }
      return { success: false, error: response.data.error }
    } catch (err) {
      console.error('Failed to delete geofence:', err)
      return { success: false, error: err.response?.data?.message || err.message }
    }
  }

  // Toggle geofence (enable/disable)
  async function toggleGeofence(id, enabled) {
    try {
      const response = await axios.patch(`${API_URL}/api/geofences/${id}/toggle`, 
        { enabled }, 
        getAuthHeaders()
      )
      if (response.data.success) {
        await fetchGeofences()
        return { success: true }
      }
      return { success: false, error: response.data.error }
    } catch (err) {
      console.error('Failed to toggle geofence:', err)
      return { success: false, error: err.response?.data?.message || err.message }
    }
  }

  // Fetch history events
  async function fetchHistory(geofenceId = null, limit = 200) {
    loading.value = true
    try {
      let url = `${API_URL}/api/geofences/history?limit=${limit}`
      if (geofenceId) {
        url += `&geofenceId=${geofenceId}`
      }
      const response = await axios.get(url, getAuthHeaders())
      if (response.data.success) {
        history.value = response.data.history || []
        
        // Log exit events for debugging
        const exitEvents = history.value.filter(h => h.event_type === 'exit')
        
        return history.value
      }
    } catch (err) {
      console.error('Failed to fetch history:', err)
      error.value = err.response?.data?.message || err.message
    } finally {
      loading.value = false
    }
    return []
  }

  // Fetch active ships inside geofences
  async function fetchActiveShips(geofenceId = null) {
    try {
      let url = `${API_URL}/api/geofences/active-ships`
      if (geofenceId) {
        url += `?geofenceId=${geofenceId}`
      }
      const response = await axios.get(url, getAuthHeaders())
      if (response.data.success) {
        activeShips.value = response.data.activeShips || []
        return activeShips.value
      }
    } catch (err) {
      console.error('Failed to fetch active ships:', err)
      error.value = err.response?.data?.message || err.message
    }
    return []
  }

  // Add alert from WebSocket
  function addAlert(alert) {
    const newAlert = {
      ...alert,
      id: Date.now(),
      timestamp: new Date().toISOString()
    }
    alerts.value.unshift(newAlert)
    
    // Keep only last 100 alerts
    if (alerts.value.length > 100) {
      alerts.value = alerts.value.slice(0, 100)
    }
    
    // Refresh active ships and history when entry/exit occurs
    if (alert.type === 'geofence_entry' || alert.type === 'geofence_exit') {
      fetchActiveShips()
      fetchHistory(null, 200) // Refresh history to get latest exit times
    }
    
  }

  // Clear all alerts
  function clearAlerts() {
    alerts.value = []
  }

  // Get geofence by id
  const getGeofenceById = computed(() => {
    return (id) => geofences.value.find(g => g.id === id)
  })

  // Get active geofences (enabled)
  const activeGeofences = computed(() => {
    return geofences.value.filter(g => g.enabled === 1)
  })

  // Get geofences for current user source
  const filteredGeofences = computed(() => {
    const userSource = authStore.userSource
    if (userSource === 'all') {
      return geofences.value
    }
    return geofences.value.filter(g => g.source === userSource || g.source === 'all')
  })

  // Get active ships count
  const activeShipsCount = computed(() => activeShips.value.length)

  // Get alerts count
  const alertsCount = computed(() => alerts.value.length)

  // Get ships with exit time (completed entries)
  const completedEntries = computed(() => {
    return history.value.filter(h => h.event_type === 'exit' && h.duration_seconds)
  })

  // Get average duration of stays
  const averageDuration = computed(() => {
    const exits = completedEntries.value
    if (exits.length === 0) return 0
    const total = exits.reduce((sum, exit) => sum + (exit.duration_seconds || 0), 0)
    return Math.floor(total / exits.length)
  })

  // Refresh all data
  async function refreshAll() {
    await Promise.all([
      fetchGeofences(),
      fetchActiveShips(),
      fetchHistory(null, 200)
    ])
  }

  // Save drawing (temporary polygon)
  async function saveDrawing(name, geojson) {
    try {
      const response = await axios.post(`${API_URL}/api/drawings`, 
        { name, geojson }, 
        getAuthHeaders()
      )
      if (response.data.success) {
        await fetchDrawings()
        return { success: true, id: response.data.id }
      }
      return { success: false, error: response.data.error }
    } catch (err) {
      console.error('Failed to save drawing:', err)
      return { success: false, error: err.response?.data?.message || err.message }
    }
  }

  // Delete drawing
  async function deleteDrawing(id) {
    try {
      const response = await axios.delete(`${API_URL}/api/drawings/${id}`, getAuthHeaders())
      if (response.data.success) {
        await fetchDrawings()
        return { success: true }
      }
      return { success: false, error: response.data.error }
    } catch (err) {
      console.error('Failed to delete drawing:', err)
      return { success: false, error: err.response?.data?.message || err.message }
    }
  }

  // Get ship entry/exit history for a specific ship
  async function getShipHistory(mmsi, limit = 50) {
    try {
      const response = await axios.get(`${API_URL}/api/geofences/history/ship/${mmsi}?limit=${limit}`, getAuthHeaders())
      if (response.data.success) {
        return response.data.history || []
      }
    } catch (err) {
      console.error('Failed to fetch ship history:', err)
      return []
    }
  }

  return {
    // State
    geofences,
    drawings,
    history,
    alerts,
    activeShips,
    loading,
    error,
    
    // Computed
    activeGeofences,
    filteredGeofences,
    activeShipsCount,
    alertsCount,
    completedEntries,
    averageDuration,
    getGeofenceById,
    
    // Actions
    fetchGeofences,
    fetchDrawings,
    fetchHistory,
    fetchActiveShips,
    saveGeofence,
    deleteGeofence,
    toggleGeofence,
    addAlert,
    clearAlerts,
    refreshAll,
    saveDrawing,
    deleteDrawing,
    getShipHistory
  }
})